import { css, CSSProps } from '@compiled/react';
import { useIntl } from 'react-intl';

import { media } from '@atlaskit/primitives/responsive';
import UFOLabel from '@atlaskit/react-ufo/label';
import { token } from '@atlaskit/tokens';

import { BgColors } from '@/libraries/design/components/link/src/link';
import { wacToken } from '@/libraries/design/foundations';
import { useTheme } from '@/libraries/design/utils/dark-theme-provider';
import {
	useLocalizedHref,
	useLocalizedHrefTrello,
} from '@/libraries/web-framework/src/localization/use-localized-href';
import { useLocation } from '@/utils/location-provider';

import { LinkListContainer } from './global-footer-top-link-list-container';

// ==================================================================
// CSS styling
// ==================================================================
const containerStyle = css({
	backgroundColor: wacToken('color.neutral.light100'),
	paddingBlock: '24px',
	paddingInline: token('space.200'),
	borderRadius: '0 0 24px 24px',
	width: '100%',
	maxWidth: '1216px',
	margin: 'auto',

	[media.only.sm]: {
		paddingInline: '44px',
	},

	[media.above.md]: {
		paddingInline: '24px',
	},
});

const containerDarkThemeStyle = css({
	backgroundColor: 'transparent',
});

const linksWrapperStyle = css({
	gridArea: 'links',
	display: 'grid',
	gridTemplateColumns: '1fr',
	gap: token('space.400'),

	[media.only.sm]: {
		gridTemplateColumns: 'repeat(2, minmax(0,1fr))',
		columnGap: '24px',
		rowGap: '48px',
	},

	[media.above.md]: {
		gridTemplateColumns: 'repeat(4, minmax(0,1fr))',
	},
});

export const ComponentStyleProps: CSSProps<unknown> = {
	margin: '0',
	padding: '0',
	border: '0',
	fontSize: '100%',
	verticalAlign: 'baseline',
};

// ==================================================================
// Rendering Functions
// ==================================================================
export const GlobalFooterTop = () => {
	const { isDarkTheme } = useTheme();
	const intl = useIntl();
	const location = useLocation();

	const productsLinkList = {
		headerText: intl.formatMessage({
			id: 'globalfooterv2.top.products',
			defaultMessage: 'Products',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.rovo',
					defaultMessage: 'Rovo',
				}),
				href: useLocalizedHref('/rovo'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.jira-software',
					defaultMessage: 'Jira',
				}),
				href: useLocalizedHref('/software/jira'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.jira-align',
					defaultMessage: 'Jira Align',
				}),
				href: useLocalizedHref('/software/jira/align'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.jira-service-management',
					defaultMessage: 'Jira Service Management',
				}),
				href: useLocalizedHref('/software/jira/service-management'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.confluence',
					defaultMessage: 'Confluence',
				}),
				href: useLocalizedHref('/software/confluence'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.loom',
					defaultMessage: 'Loom',
				}),
				href: useLocalizedHref('/software/loom'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.trello',
					defaultMessage: 'Trello',
				}),
				href: useLocalizedHrefTrello('/home'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.bitbucket',
					defaultMessage: 'Bitbucket',
				}),
				href: useLocalizedHref('/software/bitbucket'),
			},
		],
		bottomViewAllLink: {
			text: intl.formatMessage({
				id: 'globalfooterv2.top.see-all-products',
				defaultMessage: 'See all products',
			}),
			href: useLocalizedHref('/software'),
			bgColor: 'bgWhite' as BgColors,
		},
	};

	const templatesLink = {
		text: intl.formatMessage({
			id: 'globalfooterv2.top.templates',
			defaultMessage: 'Templates',
		}),
		href: useLocalizedHref('/templates'),
	};

	const resourcesLinkList = {
		headerText: intl.formatMessage({
			id: 'globalfooterv2.top.resources',
			defaultMessage: 'Resources',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.technical-support',
					defaultMessage: 'Technical support',
				}),
				href: intl.formatMessage({
					id: 'globalfooterv2.top.technical-support.link',
					defaultMessage: 'https://support.atlassian.com/',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.purchasing-and-licensing',
					defaultMessage: 'Purchasing & licensing',
				}),
				href: useLocalizedHref('/licensing/purchase-licensing'),
			},

			templatesLink,
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.atlassian-community',
					defaultMessage: 'Atlassian Community',
				}),
				href: 'https://community.atlassian.com/',
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.knowledge-base',
					defaultMessage: 'Knowledge base',
				}),
				href: intl.formatMessage({
					id: 'globalfooterv2.top.knowledge-base.link',
					defaultMessage: 'https://confluence.atlassian.com/kb',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.marketplace',
					defaultMessage: 'Marketplace',
				}),
				href: 'https://marketplace.atlassian.com/',
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.my-account',
					defaultMessage: 'My account',
				}),
				href: 'https://my.atlassian.com/products/index',
			},
		],
		bottomViewAllLink: {
			text: intl.formatMessage({
				id: 'globalfooterv2.top.create-support-ticket',
				defaultMessage: 'Create support ticket',
			}),
			href: 'https://support.atlassian.com/contact/',
			bgColor: 'bgWhite' as BgColors,
		},
	};
	const learnLinkList = {
		headerText: intl.formatMessage({
			id: 'globalfooterv2.top.learn',
			defaultMessage: 'Learn',
		}),
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.partners',
					defaultMessage: 'Partners',
				}),
				href: useLocalizedHref('/partners'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.training-and-certification',
					defaultMessage: 'Training & certification',
				}),
				href: useLocalizedHref('/university'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.documentation',
					defaultMessage: 'Documentation',
				}),
				href: intl.formatMessage({
					id: 'globalfooterv2.top.documentation.link',
					defaultMessage: 'https://confluence.atlassian.com/display/ALLDOC/Atlassian+Documentation',
				}),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.developer-resources',
					defaultMessage: 'Developer resources',
				}),
				href: useLocalizedHref('/developers'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.enterprise-services',
					defaultMessage: 'Enterprise services',
				}),
				href: useLocalizedHref('/enterprise/services'),
			},
		],
		bottomViewAllLink: {
			text: intl.formatMessage({
				id: 'globalfooterv2.top.see-all-resources',
				defaultMessage: 'See all resources',
			}),
			href: useLocalizedHref('/resources'),
			bgColor: 'bgWhite' as BgColors,
		},
	};
	const atlassianLinkList = {
		listOfLinks: [
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.company',
					defaultMessage: 'Company',
				}),
				href: useLocalizedHref('/company'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.careers',
					defaultMessage: 'Careers',
				}),
				href: useLocalizedHref('/company/careers'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.events',
					defaultMessage: 'Events',
				}),
				href: useLocalizedHref('/company/events'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.blogs',
					defaultMessage: 'Blogs',
				}),
				href: useLocalizedHref('/blog'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.investor-relations',
					defaultMessage: 'Investor Relations',
				}),
				href: 'https://investors.atlassian.com',
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.atlassian-foundation',
					defaultMessage: 'Atlassian Foundation',
				}),
				href: 'https://www.atlassianfoundation.org/',
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.press-kit',
					defaultMessage: 'Press kit',
				}),
				href: useLocalizedHref('/company/news/press-kit'),
			},
			{
				text: intl.formatMessage({
					id: 'globalfooterv2.top.contact-us',
					defaultMessage: 'Contact us',
				}),
				href: useLocalizedHref('/company/contact'),
			},
		],
	};

	// Adding pdf to french locale for compliance
	if (location.pathname.startsWith('/fr')) {
		atlassianLinkList.listOfLinks.push({
			text: `Index de l'égalité professionnelle`,

			href: 'https://dam-cdn.atl.orangelogic.com/AssetLink/qey7mu25028ek1rp0ru28l45l8y3fm5r/2025-France-Gender-Equality-Index.pdf',
		});
	}

	return (
		<UFOLabel name="global-footer-v2-top">
			<div css={[containerStyle, isDarkTheme && containerDarkThemeStyle]}>
				<div css={linksWrapperStyle}>
					<LinkListContainer {...atlassianLinkList} />
					<LinkListContainer {...productsLinkList} />
					<LinkListContainer {...resourcesLinkList} />
					<LinkListContainer {...learnLinkList} />
				</div>
			</div>
		</UFOLabel>
	);
};
